import { useState, useMemo } from "react";
import { Calculator } from "lucide-react";
import { fmtMoney } from "@/lib/constants";

export default function ReverseMathCalculator({ stats }) {
  const scheduledRate = (stats?.rates?.scheduled_rate || 68) / 100;
  const closeRate = (stats?.rates?.close_rate || 22.7) / 100;
  const defaultMonthly = stats?.avg_monthly || 2497;

  const [leadsIn, setLeadsIn] = useState(100);
  const [monthly, setMonthly] = useState(Math.round(defaultMonthly));
  const [targetMrr, setTargetMrr] = useState(50000);

  const { calls, closes, mrr, leadsNeeded } = useMemo(() => {
    const c = leadsIn * scheduledRate;
    const cl = leadsIn * closeRate;
    const m = cl * monthly;
    const need = monthly * closeRate > 0 ? Math.ceil(targetMrr / (monthly * closeRate)) : 0;
    return { calls: Math.round(c), closes: Math.round(cl * 10) / 10, mrr: Math.round(m), leadsNeeded: need };
  }, [leadsIn, monthly, targetMrr, scheduledRate, closeRate]);

  const inputCls =
    "w-full bg-[#0D1322] border border-white/10 rounded-xl px-3.5 py-2.5 text-white font-mono-g text-sm focus:outline-none focus:border-indigo-500/60 transition-colors";

  return (
    <div className="gos-card p-6 fade-up">
      <p className="eyebrow mb-1 flex items-center gap-1.5">
        <Calculator className="w-3 h-3" /> Reverse-Math Engine
      </p>
      <h2 className="font-display text-xl font-semibold text-white mb-6">MRR Forecast Calculator</h2>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <div>
          <label className="text-[11px] text-slate-400 mb-1.5 block">Leads in</label>
          <input
            data-testid="calc-leads-input"
            type="number"
            value={leadsIn}
            onChange={(e) => setLeadsIn(Number(e.target.value) || 0)}
            className={inputCls}
          />
        </div>
        <div>
          <label className="text-[11px] text-slate-400 mb-1.5 block">Monthly value ($/mo)</label>
          <input
            data-testid="calc-monthly-input"
            type="number"
            value={monthly}
            onChange={(e) => setMonthly(Number(e.target.value) || 0)}
            className={inputCls}
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 bg-[#0D1322] rounded-2xl p-4 border border-white/8 mb-4">
        {[
          { l: "Calls", v: calls, c: "#67E8F9" },
          { l: "Closes", v: closes, c: "#6EE7B7" },
          { l: "MRR", v: fmtMoney(mrr), c: "#A78BFA" },
        ].map((x) => (
          <div key={x.l} className="text-center">
            <div className="eyebrow mb-1">{x.l}</div>
            <div className="font-mono-g text-lg font-bold" style={{ color: x.c }} data-testid={`calc-out-${x.l.toLowerCase()}`}>
              {x.v}
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-end gap-3">
        <div className="flex-1">
          <label className="text-[11px] text-slate-400 mb-1.5 block">Target MRR ($/mo) → leads required</label>
          <input
            data-testid="calc-target-mrr-input"
            type="number"
            value={targetMrr}
            onChange={(e) => setTargetMrr(Number(e.target.value) || 0)}
            className={inputCls}
          />
        </div>
        <div className="text-right shrink-0">
          <div className="eyebrow mb-1">Leads Needed</div>
          <div className="font-mono-g text-2xl font-bold gos-gradient-text" data-testid="calc-leads-needed">
            {leadsNeeded}
          </div>
        </div>
      </div>
    </div>
  );
}
